import React, { forwardRef, useImperativeHandle, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { api } from '../../../services';
import { rotuloStatusDevolucaoNfce } from '../../../utils';

const useStyles = makeStyles((theme) => ({
  linha: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  rotulo: {
    minWidth: 190,
    fontWeight: 600,
    color: '#555',
  },
  valor: {
    wordBreak: 'break-all',
  },
  erro: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: '#f8d7da',
    color: '#721c24',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

function Linha({ rotulo, valor }) {
  const classes = useStyles();

  return (
    <Box className={classes.linha}>
      <span className={classes.rotulo}>{rotulo}</span>
      <span className={classes.valor}>{valor ?? '—'}</span>
    </Box>
  );
}

/*
 * Bloco "Conversão para NF-e" de uma venda: estado atual, último erro,
 * contadores das travas contra loop, chave/número da devolução e a auditoria
 * (quem converteu e qual gerente autorizou). Dados de GET /fiscal_nfces/:id/conversao.
 */
const DialogoInfoConversaoNfe = forwardRef((_props, ref) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [info, setInfo] = useState(null);
  const [falhou, setFalhou] = useState(false);

  async function carregar(id) {
    setCarregando(true);
    setFalhou(false);
    setInfo(null);

    try {
      const { data } = await api.get(`/fiscal_nfces/${id}/conversao`);
      if (data.status) {
        setInfo(data.data);
      } else {
        setFalhou(true);
      }
    } catch (e) {
      setFalhou(true);
    } finally {
      setCarregando(false);
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(id) {
      setOpen(true);
      carregar(id);
    },
  }));

  const limites = info ? info.limites : {};

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Conversão de NFC-e em NF-e</DialogTitle>
      <DialogContent>
        {carregando && (
          <Box display="flex" justifyContent="center" padding={2}>
            <CircularProgress size={28} />
          </Box>
        )}

        {falhou && (
          <Typography color="error">
            Não foi possível carregar os dados da conversão.
          </Typography>
        )}

        {info && (
          <>
            <Linha
              rotulo="Situação"
              valor={rotuloStatusDevolucaoNfce(info.status_devolucao_nfce)}
            />
            <Linha
              rotulo="Finalidade"
              valor={info.revenda ? 'Revenda' : 'Uso e consumo'}
            />
            <Linha rotulo="Convertido por" valor={info.convertido_por} />
            <Linha
              rotulo="Autorizado por (gerente)"
              valor={info.autorizado_por}
            />
            <Linha
              rotulo="Em"
              valor={
                info.conversao_nfce_em
                  ? new Date(info.conversao_nfce_em).toLocaleString('pt-BR')
                  : null
              }
            />
            <Linha
              rotulo="Ciclos de processamento"
              valor={`${info.tentativas_ciclo_devolucao_nfce} de ${limites.ciclos}`}
            />
            <Linha
              rotulo="Transmissões"
              valor={`${info.tentativas_devolucao_nfce} de ${limites.transmissoes}`}
            />
            <Linha
              rotulo="Consultas à SEFAZ"
              valor={`${info.tentativas_consulta_devolucao_nfce} de ${limites.consultas}`}
            />
            <Linha
              rotulo="Downloads do XML"
              valor={`${info.tentativas_download_xml_devolucao_nfce} de ${limites.downloads}`}
            />
            <Linha
              rotulo="XML da devolução em disco"
              valor={info.xml_devolucao_nfce_salvo ? 'Sim' : 'Não'}
            />
            <Linha
              rotulo="Chave da NFC-e original"
              valor={info.chave_de_acesso_nfce}
            />
            <Linha
              rotulo="Chave da devolução"
              valor={info.chave_acesso_devolucao_nfce}
            />
            <Linha
              rotulo="Número / série da devolução"
              valor={
                info.numero_devolucao_nfce
                  ? `${info.numero_devolucao_nfce} / ${info.serie_devolucao_nfce}`
                  : null
              }
            />
            <Linha
              rotulo="Protocolo da devolução"
              valor={info.nProt_devolucao_nfce}
            />
            {info.numeracao_invalida_devolucao_nfce ? (
              <Linha
                rotulo="Numeração"
                valor="Inválida na SEFAZ - o reinício reserva outro número"
              />
            ) : null}

            {info.erro_devolucao_nfce && (
              <Typography variant="body2" className={classes.erro}>
                {info.erro_devolucao_nfce}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
});

export default DialogoInfoConversaoNfe;
